import React, { useState } from 'react';
import { Sparkles, Clock, Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import Modal from './common/Modal';
import AiLoadingOverlay from './AiLoadingOverlay';
import axiosInstance from '../utils/axiosConfig';

const PROMPTS_BY_TYPE = {
  assets: [
    '¿Cuántos equipos hay por estado (disponible, asignado, reparación)?',
    '¿Cuáles son las marcas y modelos más comunes?',
    '¿Qué departamentos tienen más equipos asignados?',
    '¿Cuántos equipos tienen más de 3 años de antigüedad?',
    '¿Hay equipos sin activo fijo asignado?',
  ],
  smartphones: [
    '¿Cuántos smartphones hay por estado?',
    '¿Cuáles modelos de smartphone son los más comunes?',
    '¿Qué smartphones están libres y hace cuánto fueron desasignados?',
    '¿Cuántos smartphones se han rotado (más de 2 asignaciones)?',
  ],
  tablets: [
    '¿Cuántas tablets hay por estado?',
    '¿Qué tablets llevan más tiempo asignadas al mismo empleado?',
    '¿Cuántas tablets están disponibles?',
  ],
};

const TYPE_LABELS = {
  assets: 'Equipos',
  smartphones: 'Smartphones',
  tablets: 'Tablets',
};

function InventoryAISummary({ tipo, onClose }) {
  const [loading, setLoading] = useState(false);
  const [respuesta, setRespuesta] = useState(null);
  const [error, setError] = useState(null);
  const [customQuery, setCustomQuery] = useState('');
  const [history, setHistory] = useState([]);

  const executeQuery = async (pregunta) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.post('/api/ia/consulta', {
        pregunta,
        historial: history,
      });
      if (data.error) {
        setError(data.error);
      } else {
        setRespuesta(data);
        setHistory(prev => [
          ...prev,
          { role: 'user', content: pregunta },
          { role: 'assistant', content: data.texto || '' },
        ]);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al consultar la IA');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    const q = customQuery.trim();
    if (!q) return;
    setCustomQuery('');
    executeQuery(q);
  };

  const prompts = PROMPTS_BY_TYPE[tipo] || PROMPTS_BY_TYPE.assets;

  return (
    <Modal isOpen={true} onClose={onClose} size="lg">
      <Modal.Header onClose={onClose}>
        <span className="flex items-center gap-2" style={{ color: '#7c3aed' }}>
          <Sparkles size={18} />
          Resumen de inventario — {TYPE_LABELS[tipo]}
        </span>
      </Modal.Header>
      <Modal.Body>
        <div style={{ maxHeight: 'calc(70vh - 160px)', overflowY: 'auto' }}>
          {/* Sin respuesta aún — sugerencias */}
          {!respuesta && !loading && !error && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Selecciona una pregunta o escribe la tuya:</p>
              <div className="space-y-1.5">
                {prompts.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    onClick={() => executeQuery(prompt)}
                    className="block w-full text-left rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-700 hover:border-violet-300 hover:bg-violet-50/50 transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading IA */}
          {loading && (
            <AiLoadingOverlay
              inline
              phases={['Analizando inventario...', 'Consultando base de datos...', 'Generando respuesta...']}
              size="sm"
            />
          )}

          {/* Error */}
          {error && !loading && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Respuesta */}
          {respuesta && !loading && (
            <div className="space-y-4">
              <div className="text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none">
                <ReactMarkdown>{respuesta.texto}</ReactMarkdown>
              </div>

              {/* Datos */}
              {respuesta.datos && respuesta.datos.length > 0 && (
                <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                  {respuesta.datos.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-700 truncate">{item.titulo}</p>
                        {item.subtitulo && (
                          <p className="text-xs text-slate-500 truncate">{item.subtitulo}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Meta */}
              {respuesta.meta && (
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <Clock size={10} />
                    {respuesta.meta.tiempo_ms < 1000
                      ? `${respuesta.meta.tiempo_ms}ms`
                      : `${(respuesta.meta.tiempo_ms / 1000).toFixed(1)}s`
                    }
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input */}
        <form onSubmit={handleCustomSubmit} className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
          <input
            value={customQuery}
            onChange={(e) => setCustomQuery(e.target.value)}
            placeholder={respuesta ? 'Pregunta de seguimiento...' : 'Escribe tu propia pregunta...'}
            className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none placeholder:text-slate-400 focus:border-violet-300 focus:bg-white transition-colors"
          />
          <button
            type="submit"
            disabled={!customQuery.trim() || loading}
            className="flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send size={12} />
          </button>
        </form>
      </Modal.Body>
    </Modal>
  );
}

export default InventoryAISummary;
